import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  MoreContent,
  ParagraphsRenderer,
  QuickLinks,
  RelatedContent,
} from '../../../../components';
import {
  MORE_THEMES_HITS_PER_PAGE, RELATED_ARTICLES_HITS_PER_PAGE, RELATED_PROJECTS_HITS_PER_PAGE, RELATED_TEAM_HITS_PER_PAGE,
} from '../../../../config/constants';
import type { TaxonomyTheme } from '../../../../types/uncinc_profile/taxonomy';

import './style.scss';

interface ThemeProps {
  entity: ImmutableEntity<TaxonomyTheme>;
  className?: string;
}

const TaxonomyThemePage: React.FC<ThemeProps> = ({ entity, className }) => {
  const { t } = useTranslation();

  const theme = entity.toJS();
  const tid = theme.tid[0].value;
  const langcode = theme.langcode[0].value;
  const title = theme.name[0].value;
  const body = theme.description[0]?.processed;
  const media = theme.field_media_item[0];
  const caption = media?.entity?.field_caption[0]?.value;
  const paragraphs = theme.field_paragraphs;

  const classes = classNames('Taxonomy', 'TaxonomyTheme', className);

  const quickLinks = [
    {
      title: t('components.quicklinks.projects'),
      link: '#projects',
      className: 'quicklink__projects',
    },
    {
      title: t('components.quicklinks.news'),
      link: '#articles',
      className: 'quicklink__news',
    },
    {
      title: t('components.quicklinks.experts'),
      link: '#experts',
      className: 'quicklink__experts',
    },
  ];

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="TaxonomyTheme__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-8">
              <h1 className="TaxonomyTheme__title" itemProp="title">
                {title}
              </h1>

              {body && (
                <WysiwygField
                  className="TaxonomyTheme__body body--large"
                  property="schema:text"
                  html={body}
                />
              )}
            </div>
            <div className="col-12 col-md-4 col-lg-3 offset-lg-1">
              <div className="TaxonomyTheme__quicklinks">
                <QuickLinks quickLinks={quickLinks} />
              </div>
            </div>
            {media && (
              <div className="col-12">
                <div className="TaxonomyTheme__media-wrapper">
                  <DynamicMediaItem
                    className="TaxonomyTheme__media"
                    media={media}
                    imageAspectRatio="2_1"
                  />
                  {/* We render the caption outside, to prevent layout shifting */}
                  {caption && (
                    <p className="caption">{caption}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <ParagraphsRenderer
        className="TaxonomyTheme__paragraphs"
        paragraphs={paragraphs}
        isWide
      />

      <div className="FixedReferenceBlocks">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10">
              <div className="TaxonomyTheme__projects">
                <div id="projects" className="TaxonomyTheme__anchor" />
                <RelatedContent
                  title={t('content_types.project.related_content')}
                  contentType="project"
                  hitsPerPage={RELATED_PROJECTS_HITS_PER_PAGE}
                  filterNames={['themes_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                />
              </div>
              <div className="TaxonomyTheme__articles">
                <div id="articles" className="TaxonomyTheme__anchor" />
                <RelatedContent
                  title={t('content_types.article.related_content')}
                  contentType="article"
                  hitsPerPage={RELATED_ARTICLES_HITS_PER_PAGE}
                  filterNames={['themes_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                />
              </div>
              <div className="TaxonomyTheme__experts">
                <div id="experts" className="TaxonomyTheme__anchor" />
                <RelatedContent
                  title={t('components.quicklinks.experts')}
                  contentType="profile"
                  hitsPerPage={RELATED_TEAM_HITS_PER_PAGE}
                  filterNames={['profile_themes_tid']}
                  filterValues={[tid]}
                  langcode={langcode}
                />
              </div>
              <div className="TaxonomyTheme__other-themes">
                <div id="themes" className="TaxonomyTheme__anchor" />
                <MoreContent
                  title={t('taxonomies.themes.other_title')}
                  contentType="themes"
                  hitsPerPage={MORE_THEMES_HITS_PER_PAGE}
                  filterName="tid"
                  filterValue={tid}
                  langcode={langcode}
                  buttonUrl={t('taxonomies.themes.other_button_url')}
                  buttonText={t('taxonomies.themes.other_button_text')}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default TaxonomyThemePage;
